import { useId, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { FormFieldContext } from './form-field-context';
import { Label } from './Label';

export interface FormFieldProps {
  /** Widoczna etykieta pola. */
  label?: ReactNode;
  /** Podpowiedz pod polem. Ukrywana, gdy pojawi sie blad. */
  hint?: ReactNode;
  /** Komunikat bledu walidacji. Jego obecnosc wlacza stan `invalid`. */
  error?: string | undefined;
  required?: boolean;
  /** Wlasny identyfikator kontrolki. Domyslnie generowany. */
  id?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Opakowanie pola formularza: etykieta, kontrolka, podpowiedz i blad.
 *
 * Powiazania dostepnosciowe ustawiane sa automatycznie — kontrolka w srodku
 * odbiera je przez kontekst.
 */
export function FormField({
  label,
  hint,
  error,
  required = false,
  id,
  className,
  children,
}: FormFieldProps) {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const errorId = `${controlId}-error`;
  const hintId = `${controlId}-hint`;

  const invalid = Boolean(error);
  const describedBy = invalid ? errorId : hint ? hintId : undefined;

  return (
    <FormFieldContext.Provider value={{ controlId, describedBy, invalid, required }}>
      <div className={cn('flex flex-col gap-1.5', className)}>
        {label && (
          <Label htmlFor={controlId} required={required}>
            {label}
          </Label>
        )}

        {children}

        {invalid ? (
          <p id={errorId} role="alert" className="text-danger-500 text-xs font-medium">
            {error}
          </p>
        ) : (
          hint && (
            <p id={hintId} className="text-fg-subtle text-xs">
              {hint}
            </p>
          )
        )}
      </div>
    </FormFieldContext.Provider>
  );
}
